import { createBrowserHistory } from 'history';
import { NavigationPages } from '../consts';
import { stringify } from 'query-string';

export const browserHistory = createBrowserHistory();

export const projectIdParameter = 'projectId';
const projectIdParameterDefinition = `:${projectIdParameter}`;
export const packageIdParameter = 'packageId';
const packageIdParameterDefinition = `:${packageIdParameter}`;
export const itemIdParameter = 'itemId';
const itemIdParameterDefinition = `:${itemIdParameter}`;
export const attachmentIdParameter = 'attachmentId';
const attachmentIdParameterDefinition = `:${attachmentIdParameter}`;

let projectId = projectIdParameterDefinition;
export const setProjectId = (pId) => {
  projectId = pId;
};
export const getProjectId = () => projectId;

/** Route definitions for react router **/
export const baseURLExtension = '/build/submittals/projects';
export const BaseRouteDefinition = `${baseURLExtension}/${projectIdParameterDefinition}`;
export const ItemsCollectionRouteDefinition = `${BaseRouteDefinition}/${NavigationPages.ITEMS}`;
export const ItemDetailsRouteDefinition = `${BaseRouteDefinition}/${NavigationPages.ITEMS}/${itemIdParameterDefinition}`;
export const PdfRouteDefinition = `${ItemDetailsRouteDefinition}/${NavigationPages.ATTACHMENTS}/${attachmentIdParameterDefinition}`;
export const DraftPdfRouteDefinition = `${ItemDetailsRouteDefinition}/${NavigationPages.DRAFTS}/${attachmentIdParameterDefinition}`;
export const PackagesCollectionRouteDefinition = `${BaseRouteDefinition}/${NavigationPages.PACKAGES}`;
export const PackageDetailsRouteDefinition = `${BaseRouteDefinition}/${NavigationPages.PACKAGES}/${packageIdParameterDefinition}`;
export const SpecSectionsCollectionRouteDefinition = `${BaseRouteDefinition}/${NavigationPages.SPEC_SECTIONS}`;
export const ProjectSettingsRouteDefinition = `${BaseRouteDefinition}/${NavigationPages.SETTINGS}`;
export const PermissionsProjectSettingsRouteDefinition = `${ProjectSettingsRouteDefinition}/permissions`;
export const DefaultValuesSettingsRouteDefinition = `${ProjectSettingsRouteDefinition}/defaults`;
export const CustomResponsesProjectSettingsRouteDefinition = `${ProjectSettingsRouteDefinition}/responses`;
export const CustomTypesProjectSettingsRouteDefinition = `${ProjectSettingsRouteDefinition}/types`;
export const ReviewTemplatesRouteDefinition = `${ProjectSettingsRouteDefinition}/templates`;
export const CustomFieldsRouteDefinition = `${ProjectSettingsRouteDefinition}/custom-fields`;
export const AdvancedSettingsRouteDefinition = `${ProjectSettingsRouteDefinition}/advanced`;
export const AutospecsRouteDefination = `/autospecs/home/projects/${projectIdParameterDefinition}`;
/** Navigable routes **/

/**
 *
 * @param route - route definition (with possible route parameters)
 * @param keyValuePairs - parameters that will be populated in the route. Will populate ":projectId" by default
 */
export const getNavigableRoute = (route, keyValuePairs = {}) => {
  if (!keyValuePairs[projectIdParameterDefinition]) {
    keyValuePairs[projectIdParameterDefinition] = projectId;
  }

  let navigableRoute = route;
  for (const [parameterKey, parameterValue] of Object.entries(keyValuePairs)) {
    navigableRoute = navigableRoute.replace(parameterKey, parameterValue);
  }

  return navigableRoute;
};

export const AutospecsRoute = () => {
  return getNavigableRoute(AutospecsRouteDefination);
};

export const ItemsCollectionRoute = () => {
  return getNavigableRoute(ItemsCollectionRouteDefinition);
};

export const PackagesCollectionRoute = () => {
  return getNavigableRoute(PackagesCollectionRouteDefinition);
};

export const SpecSectionsCollectionRoute = () => {
  return getNavigableRoute(SpecSectionsCollectionRouteDefinition);
};

export const PackageRoute = (packageId) => {
  return getNavigableRoute(PackageDetailsRouteDefinition, {
    [packageIdParameterDefinition]: packageId,
  });
};

export const PdfDocumentRoute = (attachmentId, itemId, isDraft) => {
  return getNavigableRoute(isDraft ? DraftPdfRouteDefinition : PdfRouteDefinition, {
    [attachmentIdParameterDefinition]: attachmentId,
    [itemIdParameterDefinition]: itemId,
  });
};

export const ItemDetailsRoute = (itemId) => {
  return getNavigableRoute(ItemDetailsRouteDefinition, {
    [itemIdParameterDefinition]: itemId,
  });
};

/** Settings */
export const PermissionsProjectSettingsRoute = () => {
  return getNavigableRoute(PermissionsProjectSettingsRouteDefinition);
};

export const DefaultValuesSettingsRoute = () => {
  return getNavigableRoute(DefaultValuesSettingsRouteDefinition);
};

export const CustomResponsesProjectSettingsRoute = () => {
  return getNavigableRoute(CustomResponsesProjectSettingsRouteDefinition);
};

export const CustomTypesProjectSettingsRoute = () => {
  return getNavigableRoute(CustomTypesProjectSettingsRouteDefinition);
};

export const ReviewTemplatesRoute = () => getNavigableRoute(ReviewTemplatesRouteDefinition);

export const CustomFieldsRoute = () => getNavigableRoute(CustomFieldsRouteDefinition);

export const AdvancedSettingsRoute = () => getNavigableRoute(AdvancedSettingsRouteDefinition);

export const HomepageRoute = () => ItemsCollectionRoute();
export const ProjectSettingsHomepageRoute = () => PermissionsProjectSettingsRoute();

export const getPathWithQueryParams = (path, queryParams) => {
  if (!queryParams) return path;
  const queryString = stringify(queryParams);
  return `${path}?${queryString}`;
};

// Return the current url pathname (no host or query params)
export const getPathname = () => window.location.pathname;

export const getQueryParams = () => {
  const query = decodeURIComponent(window.location.search);

  if (!query) {
    return {};
  }

  return (/^[?#]/.test(query) ? query.slice(1) : query).split('&').reduce((params, param) => {
    const split = param.split('=');
    const key = split[0];
    const value = split[1];

    params[key] = params[key] ? params[key] : [];

    if (value) {
      params[key].push(value.replace(/\+/g, ' '));
    }

    return params;
  }, {});
};

export const getPathWithParams = () => {
  return { path: getPathname(), queryParams: getQueryParams() };
};

export const replaceState = (path, queryParams) => {
  browserHistory.replace(getPathWithQueryParams(path, queryParams));
};

export function pushState(path, queryParams) {
  browserHistory.push(getPathWithQueryParams(path, queryParams), {
    prevLocation: getPathWithParams(),
  });
}

export function openPathInNewTab(path, queryParams) {
  const url = getPathWithQueryParams(path, queryParams);
  window.open(url, '_blank');
}

export const createPath = (pathname, queryParams) => {
  const path = queryParams ? `${pathname}?${stringify(queryParams)}` : pathname;
  return window.location.origin + path;
};

const getQueryParamsWithAddedParams = (paramsToAdd) => ({
  ...getQueryParams(),
  ...paramsToAdd,
});

export const addQueryParams = (params) => {
  replaceState(window.location.pathname, getQueryParamsWithAddedParams(params));
};

export function removeQueryParams(paramName) {
  const queryParams = getQueryParams();
  delete queryParams[paramName];

  replaceState(window.location.pathname, queryParams);
}

/**
 * take your URL and adds '?create=true' at the end
 * @private
 */
export const showCreateModal = () => {
  addQueryParams({ create: true });
};

/**
 * take your URL and add '?import=true' at the end
 * @private
 */
export const showImportModal = () => {
  addQueryParams({ import: true });
};

/**
 * take your URL and remove'?import=true' at the end
 * @private
 */
export const hideImportModal = () => {
  removeQueryParams('import');
};

/**
 * take your URL and remove '?create=true'
 * @private
 */
export const hideCreateModal = () => {
  removeQueryParams('create');
};

export const getSpecSectionPreviewHref = (specSectionId) => {
  const queriesToAdd = { preview: specSectionId };
  const newQueriesObj = getQueryParamsWithAddedParams(queriesToAdd);
  return createPath(window.location.pathname, newQueriesObj);
};

/**
 * take your URL and adds '?preview={specSectionId}' at the end
 * @private
 */
export const showSpecSectionPreviewModal = (specSectionId) => {
  addQueryParams({ preview: specSectionId });
};

/**
 * take your URL and remove '?preview={specSectionId}'
 * @private
 */
export const hideSpecSectionPreviewModal = () => {
  removeQueryParams('preview');
};

export const getErrorPageUrl = () => {
  return '/error-page.html';
};

export const isItemsCollectionWithItemPreviewRoute = () => {
  const { path, queryParams } = getPathWithParams();
  return path.includes(`/${NavigationPages.ITEMS}`) && Object.keys(queryParams).includes('preview');
};

export const isAttachmentsRoute = (pathname = getPathname()) => {
  return pathname.includes(`/${NavigationPages.ATTACHMENTS}`) || pathname.includes(`/${NavigationPages.DRAFTS}`);
};

export const isItemsCollectionRoute = (pathname = getPathname()) => {
  return pathname.includes(`/${NavigationPages.ITEMS}`);
};

export const isItemDetailsRoute = (pathname = getPathname()) => {
  return pathname.includes(`/${NavigationPages.ITEMS}/`);
};

export const isPackageDetailsRoute = (pathname = getPathname()) => {
  return pathname.includes(`/${NavigationPages.PACKAGES}/`);
};

const isPackageDetailsWithItemPreviewRoute = () => {
  const { queryParams } = getPathWithParams();
  return isPackageDetailsRoute() && Object.keys(queryParams).includes('preview');
};

export const isPackagesCollectionRoute = (pathname = getPathname()) => {
  return pathname.includes(`/${NavigationPages.PACKAGES}`);
};

const isSpecSectionsCollectionRoute = (pathname = getPathname()) => {
  return pathname.includes(`/${NavigationPages.SPEC_SECTIONS}`);
};

const isProjectSettingsRoute = (pathname = getPathname()) => {
  return pathname.includes(`/${NavigationPages.SETTINGS_PERMISSIONS}`);
};

export const getCurrentPageFromUrl = () => {
  const pathname = getPathname();

  if (isProjectSettingsRoute(pathname)) return NavigationPages.SETTINGS_PERMISSIONS;
  if (isItemsCollectionRoute(pathname)) return NavigationPages.ITEMS;
  if (isPackageDetailsRoute(pathname)) return NavigationPages.PACKAGE_VIEW;
  if (isPackagesCollectionRoute(pathname)) return NavigationPages.PACKAGES;
  if (isSpecSectionsCollectionRoute(pathname)) return NavigationPages.SPEC_SECTIONS;

  return null;
};

export const getPreviewParam = () => {
  const { queryParams } = getPathWithParams();
  return queryParams.preview;
};

export const isItemModalRoute = () => {
  return isItemsCollectionWithItemPreviewRoute() || isPackageDetailsWithItemPreviewRoute();
};

export const getPackageId = (path) => {
  const packagesStr = `${NavigationPages.PACKAGES}/`;
  const packagesIndex = path.indexOf(packagesStr);
  if (packagesIndex === -1) {
    return null;
  }
  return path.substring(packagesIndex + packagesStr.length);
};
